import { FavoritesAuthGuard } from "app/components/auth/favorites-auth-guard.component";
import MemoFavoritesNoAccount from "app/components/icons/FavoritesNoAccount";
import { PodcastList } from "app/components/podcats-list/podcast-list.component";
import { StationsList } from "app/components/stations-list/stations-list.component";
import NoSsr from "app/components/no-ssr.component";
import { selectUserState, userActions } from "app/store/slices/user";
import { useAppDispatch, useAppSelector } from "app/store/store";
import type { NextPage } from "next";
import React, { useEffect, useMemo, useState } from "react";
import styles from "./index.module.scss";
import { playerActions, selectPlayerState } from "app/store/slices/player";
import { useFavorites } from "app/hooks/favorites.hook";
import { usePlayerContext } from "app/context/player.context";
import { useGoBackHistory } from "app/hooks/go-back.hook";
import { useRouter } from "next/router";
import clsx from "clsx";
import MemoHeartOutline from "app/components/icons/HeartOutline";
import { useTranslate } from "app/hooks/lang";
import { NextSeo } from "next-seo";

const Home: NextPage = () => {
  const router = useRouter();
  const { permalink, toggle, autoplay } = router.query;
  const { setPlayerEnabled } = usePlayerContext();
  const { session, favorites, favoritesLoading } =
    useAppSelector(selectUserState);
  const [autoplayEnabled, setAutoplayEnabled] = useState(false);
  const { autoPermalink, autoToggle, autoPlay } = useMemo(() => {
    return {
      autoPermalink: (permalink as string) || "",
      autoToggle: (toggle as string) || "",
      autoPlay: (autoplay as string) || "",
    };
  }, [permalink, toggle, autoplay]);

  const { toggleFavorite } = useFavorites();
  useEffect(() => {
    if (autoToggle === "true") {
      toggleFavorite({ permalink: autoPermalink });
    }
    if (autoplay === "true") {
      setAutoplayEnabled(true);
    }
  }, [autoPermalink, autoToggle, autoplay]);

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (session?.user) {
      dispatch(userActions.loadFavorites());
    }
  }, [session, dispatch]);

  const { failedPlaybackPermalink, activeMedia } =
    useAppSelector(selectPlayerState);

  const stations = useMemo(
    () => favorites.filter((category) => category.type === "radio"),
    [favorites]
  );
  const podcasts = useMemo(
    () => favorites.filter((category) => category.type === "podcast"),
    [favorites]
  );
  useEffect(() => {
    if (autoplayEnabled && session?.user && stations.length > 0) {
      let playIndex = 0;
      let play = true;
      if (failedPlaybackPermalink) {
        let permalinks = stations.map((item) => item.permalink);
        let found = permalinks.lastIndexOf(failedPlaybackPermalink);

        if (found > -1 && found < stations.length - 1) {
          playIndex = found + 1;
        } else if (found === stations.length - 1) {
          play = false;
        }
      }

      if (play) {
        if (
          activeMedia?.category?.permalink === stations[playIndex].permalink
        ) {
          setAutoplayEnabled(false);
        }
        dispatch(
          playerActions.setRadioQueue({
            items: stations,
            activeIndex: playIndex,
          })
        );
        setPlayerEnabled(true);
        dispatch(
          playerActions.selectTrack({
            podcast: null,
            category: stations[playIndex],
          })
        );
      }
    }
  }, [
    favorites,
    autoplayEnabled,
    failedPlaybackPermalink,
    activeMedia?.category?.permalink,
  ]);
  useGoBackHistory("FAVORITES");
  const t = useTranslate();
  return (
    <>
      <NextSeo
        title={t("header.menu.favorites")}
        titleTemplate={`%s - Radioline`}
        description={t("header.menu.favorites") + " - Radioline"}
        openGraph={{
          images: [
            {
              url: "https://www.radioline.co/open-graph.png",
              alt: "Radioline",
            },
          ],
        }}
      />
      <div className={styles.container}>
        <NoSsr>
          {session?.user ? (
            favorites.length > 0 ? (
              <div>
                <div className={styles.stations_wrapper}>
                  <StationsList
                    items={stations}
                    label={t("tabs.stations")}
                    loading={favoritesLoading}
                    rows={2}
                  />
                </div>
                <PodcastList
                  items={podcasts}
                  label={t("tabs.podcasts")}
                  loading={favoritesLoading}
                  rows={3}
                  itemClassName={styles.favorites_podcast}
                />
              </div>
            ) : (
              <div className={styles.no_account}>
                <div className={styles.icon}>
                  <MemoFavoritesNoAccount />
                </div>
                <div className={styles.content}>
                  <h3 className={styles.title}>{t("header.menu.favorites")}</h3>
                  <p className={styles.subtitle}>
                    {t("favorites.no-favotites")}
                  </p>
                  <p className={clsx(styles.subtitle, styles.with_icon)}>
                    <div className={styles.icon_wrapper}>
                      <MemoHeartOutline />
                    </div>
                    {t("search-for-stations")}
                  </p>
                </div>
              </div>
            )
          ) : (
            <FavoritesAuthGuard />
          )}
        </NoSsr>
      </div>
    </>
  );
};

export default Home;
